interface ZakekeMessageCommon {
    readonly zakekeMessageType: number | string;
}

interface ZakekeMessageCustomizerAddToCart extends ZakekeMessageCommon {
    readonly zakekeMessageType: 0;
    readonly designId: string;
}

interface ZakekeMessageComposerAddToCart {
    readonly zakekeMessageType: 'AddToCart';
    readonly message: {
        readonly composition: string;
        readonly preview?: string;
        readonly quantity?: number;
    };
}

interface ZakekeMessageDesignChange extends ZakekeMessageCommon {
    readonly zakekeMessageType: 1;
    readonly design?: {
        readonly color?: string | null;
    };
}

interface ZakekeMessageCustomizerProductAttributes extends ZakekeMessageCommon {
    readonly zakekeMessageType: 2;
    readonly data?: {
        readonly promiseId?: string | number | null;
    };
}

interface ZakekeMessageCustomizerProductPrice extends ZakekeMessageCommon {
    readonly zakekeMessageType: 3;
    readonly data?: {
        readonly promiseId?: string | number | null;
    };
}

// Discovery mode channel (mirrors the customizer's useDiscoveryModeUpdater contract).
// Iframe -> backoffice: the customizer announces it has booted in discovery mode and is listening.
interface ZakekeMessageDiscoveryModeReady extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'DiscoveryModeReady';
}

// Backoffice -> iframe: stream a feature selection (print method / pricing rule / AI / tool)
// to apply to the previewed product without reloading. Payload shape is finalized in a later step.
export interface ZakekeMessageDiscoveryModeUpdate extends ZakekeMessageCommon {
    readonly zakekeMessageType: 'DiscoveryModeUpdate';
    readonly category: 'printing' | 'pricing' | 'ai' | 'tools' | 'quantity';
    readonly payload: unknown;
}

export type ZakekeMessage =
    | ZakekeMessageCommon
    | ZakekeMessageCustomizerAddToCart
    | ZakekeMessageComposerAddToCart
    | ZakekeMessageDesignChange
    | ZakekeMessageCustomizerProductAttributes
    | ZakekeMessageCustomizerProductPrice
    | ZakekeMessageDiscoveryModeReady
    | ZakekeMessageDiscoveryModeUpdate;

export const isCustomizerAddToCartMessage = (message: any): message is ZakekeMessageCustomizerAddToCart => message?.zakekeMessageType === 0;

export const isComposerAddToCartMessage = (message: any): message is ZakekeMessageComposerAddToCart =>
    message?.zakekeMessageType === 'AddToCart' && !!message?.message?.composition;

/** @deprecated Use isCustomizerAddToCartMessage instead */
export const isAddToCartMessage = isCustomizerAddToCartMessage;

export const isDesignChangeMessage = (message: ZakekeMessage): message is ZakekeMessageDesignChange => message.zakekeMessageType === 1;

export const isCustomizerProductAttributesMessage = (message: ZakekeMessage): message is ZakekeMessageCustomizerProductAttributes =>
    message.zakekeMessageType === 2;

export const isCustomizerProductPriceMessage = (message: ZakekeMessage): message is ZakekeMessageCustomizerProductPrice => message.zakekeMessageType === 3;

export const isDiscoveryModeReadyMessage = (message: any): message is ZakekeMessageDiscoveryModeReady => message?.zakekeMessageType === 'DiscoveryModeReady';
